import { useState } from 'react';
import { createPortal } from 'react-dom';
import { isAxiosError } from 'axios';
import { Icon } from '@/components/Icon';
import { InfoRow } from '@/components/InfoRow';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import type { AdminUserDetail, AdminUserOptions, SaveAdminUserPayload } from '@/api/adminUsers';
import { ProfileFields, SsoFields, RolesFields } from './AdminFields';
import { usePermissionCatalog } from './usePermissionCatalog';
import {
  adminStateEquals,
  adminStateFromDetail,
  adminStateToPayload,
  validateAdminPassword,
  type AdminFormState,
} from './adminFormState';

type Tab = 'profile' | 'sso' | 'roles';

const TABS: { key: Tab; label: string }[] = [
  { key: 'profile', label: 'Информация профиля' },
  { key: 'sso', label: 'Единый вход' },
  { key: 'roles', label: 'Роли и права' },
];

/**
 * Карточка существующего администратора — 1:1 набор блоков старого Orchid
 * UserEditScreen, разложенный по вкладкам: профиль (UserEditLayout +
 * UserStatusLayout), единый вход (UserPasswordLayout + UserSyncLayout) и роли
 * с правами (UserRoleLayout + RolePermissionLayout).
 *
 * readOnly относится ТОЛЬКО к профилю (его и включает кнопка «Редактировать»
 * в шапке): единый вход и роли — настройки доступа, у них нет режима
 * просмотра, они правятся всегда и сохраняются кнопкой внизу своей вкладки.
 *
 * Нового администратора заводит AdminCreateWizard — там шаги, а не вкладки.
 */
export function AdminUserForm({
  initial,
  options,
  readOnly = false,
  actionsSlot,
  onSave,
  onCancel,
}: {
  initial: AdminUserDetail;
  options: AdminUserOptions | null;
  readOnly?: boolean;
  /** Узел в шапке страницы под «Отмена»/«Сохранить» — там же, где «Редактировать». */
  actionsSlot?: HTMLElement | null;
  onSave: (payload: SaveAdminUserPayload) => Promise<void>;
  onCancel: () => void;
}) {
  const pristine = adminStateFromDetail(initial);
  const [tab, setTab] = useState<Tab>('profile');
  const [state, setState] = useState<AdminFormState>(pristine);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { catalog, error: catalogError } = usePermissionCatalog();
  const { confirm, dialog } = useConfirmDialog();

  const statusLabel = options?.statuses.find((s) => s.value === state.status)?.label ?? state.status;
  const roleNames = (options?.roles ?? [])
    .filter((r) => state.roleIds.includes(Number(r.value)))
    .map((r) => r.label);

  /** Вкладки — независимые блоки настроек, поэтому уход с несохранённой правки спрашиваем. */
  const switchTab = async (next: Tab) => {
    if (next === tab) return;
    if (!adminStateEquals(state, pristine)) {
      const ok = await confirm('Перейти на другую вкладку? Несохранённые изменения будут потеряны.', {
        confirmLabel: 'Перейти',
      });
      if (!ok) return;
      setState(pristine);
      setFieldErrors([]);
      setError(null);
    }
    setTab(next);
  };

  const submit = async () => {
    const missing: string[] = [];
    if (!state.name.trim()) missing.push('name');
    if (!state.email.trim()) missing.push('email');

    setFieldErrors(missing);
    if (missing.length > 0) {
      setTab('profile');
      setError('Заполните обязательные поля: название и электронная почта.');
      return;
    }

    // Роли обязательны — 1:1 валидация UserEditScreen::save().
    if (state.roleIds.length === 0) {
      setTab('roles');
      setError('Выберите хотя бы одну роль.');
      return;
    }

    const passwordError = validateAdminPassword(state);
    if (passwordError) {
      setTab('sso');
      setError(passwordError);
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await onSave(adminStateToPayload(state, initial.login));
    } catch (err) {
      const detail = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
      setError(detail || 'Не удалось сохранить администратора.');
    } finally {
      setSaving(false);
    }
  };

  const fieldsProps = { state, onChange: setState, options, disabled: saving, fieldErrors };

  const profileActions = (
    <>
      <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>
        Отмена
      </button>
      <button type="button" className="btn btn--primary" onClick={submit} disabled={saving}>
        Сохранить
      </button>
    </>
  );

  return (
    <div className={`operator-template-form${saving ? ' operator-template-form--saving' : ''}`} aria-busy={saving}>
      {/* Правка профиля — «Отмена»/«Сохранить» уезжают в шапку страницы, на место
          кнопки «Редактировать». Слота нет (например в тестах) — рисуем на месте. */}
      {!readOnly &&
        tab === 'profile' &&
        (actionsSlot ? (
          createPortal(profileActions, actionsSlot)
        ) : (
          <div className="operator-template-form__actions operator-template-form__actions--top">
            {profileActions}
          </div>
        ))}

      {(error || catalogError) && <p className="field-error">{error ?? catalogError}</p>}

      <div className="tabs__list" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`tabs__item${tab === t.key ? ' tabs__item--active' : ''}`}
            onClick={() => void switchTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="tabs__panel" role="tabpanel">
        {/* Просмотр профиля — карточками, как «Общее» в карточке клиента. */}
        {tab === 'profile' && readOnly && (
          <div className="detail__grid-2">
            <div className="card">
              <h2 className="detail__subtitle">Информация профиля</h2>
              <dl className="info-list">
                <InfoRow label="Название" value={state.name || null} />
                <InfoRow label="Фамилия" value={state.lastName || null} />
                <InfoRow label="Имя" value={state.firstName || null} />
                <InfoRow label="Отчество" value={state.surname || null} />
                <div className="info-list__row">
                  <dt>Статус кабинета</dt>
                  <dd>
                    <span className={`badge badge--${state.status === 'active' ? 'good' : 'neutral'}`}>
                      <span className="badge__dot" />
                      {statusLabel}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            <div className="card">
              <h2 className="detail__subtitle">Контакты</h2>
              {state.email ? (
                <div className="contact-row">
                  <Icon name="mail" size={16} />
                  <a href={`mailto:${state.email}`}>{state.email}</a>
                </div>
              ) : (
                <p className="cell-muted">Электронная почта не указана.</p>
              )}
              {roleNames.length > 0 && <p className="cell-muted">{roleNames.join(', ')}</p>}
            </div>
          </div>
        )}

        {tab === 'profile' && !readOnly && <ProfileFields {...fieldsProps} />}
        {tab === 'sso' && <SsoFields {...fieldsProps} login={initial.login ?? ''} />}
        {tab === 'roles' && <RolesFields {...fieldsProps} catalog={catalog} />}
      </div>

      {/* Единый вход и роли правятся всегда — сохранение под своей вкладкой. */}
      {tab !== 'profile' && (
        <div className="operator-template-form__actions">
          <button type="button" className="btn btn--primary" onClick={submit} disabled={saving}>
            Сохранить
          </button>
        </div>
      )}

      {dialog}
    </div>
  );
}
